import React, { useContext, useState, useEffect } from "react";
import { TranslationsContext } from "../../../utils/TranslationsContext";
import AccreditationInput from "../../AccreditationInput/AccreditationInput";
import AddPicture from "./AddPicture";
import Loader from "../../Loader/Loader";
import AccreditationSelector from "./AccreditationSelector";
import { apiRoutes } from "../../../utils/apiRoutes";
import { useNavigate } from "react-router-dom";
import { useAlert } from "../../../utils/AlertContext";
import { trimValues } from "../../../utils/trimValues";
import {
  createData,
  updateData,
  deleteData,
  fetchData,
} from "../../../utils/methods";
import HistoryPanel from "../../HistoryPanel/HistoryPanel";
import EditModalShell from "./EditModalShell";
import ConfirmationModal from "./ConfirmationModal";
import useEnterKeySubmit from "../../../hooks/useEnterKeySubmit";

// Build-time literal (unlike apiRoutes) so Terser can prove the History
// branch below is dead code on tiers other than Third. See AddGroup.jsx.
const TIER = process.env.REACT_APP_MODE;

// Matches defaultOptions in gs.akr/helpers/queue.js — a field absent from a
// group's stored dizajnOpcije still defaults to true there when the PDF is
// actually generated (spread merge: {...defaultOptions, ...options}), so an
// older group saved before a field existed silently prints it anyway while
// showing no toggle for it here at all.
const DEFAULT_DIZAJN_OPCIJE = {
  qr: true,
  ime: true,
  prezime: true,
  pozicija: true,
  entitet: true,
  akreditacija: true,
  slika: true,
};

// 'grupa' was dropped as a print option (gs.rest/routes/validation.js no
// longer allows it — pdf.js never rendered it on the card). Strip it from
// any dizajnOpcije loaded from older saved groups so re-saving the group
// unchanged doesn't resend a now-rejected field. Also backfill any keys
// missing entirely (older groups saved before that field existed) against
// the same defaults gs.akr merges in at generation time — otherwise
// AccreditationSelector, which renders one row per key actually present,
// silently omits a toggle for a field that's live (defaulting true) on the
// printed card.
const sanitizeDizajnOpcije = (opcije) => {
  if (!opcije) return { ...DEFAULT_DIZAJN_OPCIJE };
  const { grupa, ...rest } = opcije;
  return { ...DEFAULT_DIZAJN_OPCIJE, ...rest };
};

const EventGroup = ({
  editing,
  onCloseClick,
  object,
  eventId,
  handleAddData,
  handleUpdateData,
  handleDeleteData,
  parent,
}) => {
  const { t } = useContext(TranslationsContext);
  const [group, setGroup] = useState(
    object
      ? { ...object, dizajnOpcije: sanitizeDizajnOpcije(object.dizajnOpcije) }
      : {
          // A new group shows everything on the card unless the admin opts out.
          dizajnOpcije: { ...DEFAULT_DIZAJN_OPCIJE },
          emailTemplateId: "",
          dizajnTemplate: "",
          parentDizajn: false,

          // korisnici: [],
          dogadjajId: eventId,
          naziv: "",
          skracenica: "",
          opis: "",
          parentId: parent ? parent.id : null,
        },
  );
  const [errors, setErrors] = useState({});
  const [imageLoading, setImageLoading] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isConfirmDeleteOpen, setIsConfirmDeleteOpen] = useState(false);
  const navigate = useNavigate();
  const { showAlert } = useAlert();
  const [templates, setTemplates] = useState([]);

  useEffect(() => {
    const fetchTemplates = async () => {
      try {
        setIsLoading(true);

        const result = await fetchData(
          `${apiRoutes.EmailTemplate}`,
          showAlert,
          navigate,
          t,
        );
        if (!result) return;

        setTemplates(result.data);
      } catch (err) {
        console.error("Error fetching countries:", err);
      } finally {
        setIsLoading(false);
      }
    };

    fetchTemplates();
  }, []);

  const setInputValue = (key, value) => {
    if (key === "slika") {
      setGroup((prev) => ({ ...prev, dizajnTemplate: value }));
      return;
    }

    if (key === "parentDizajn") {
      setGroup((prev) => {
        const next = { ...prev, parentDizajn: value };
        if (value && parent) {
          next.dizajnOpcije = sanitizeDizajnOpcije(
            parent.dizajnOpcije ?? prev.dizajnOpcije,
          );
          next.dizajnTemplate = parent.dizajnTemplate ?? prev.dizajnTemplate;
        }
        return next;
      });
      return;
    }

    setGroup((prevValues) => ({ ...prevValues, [key]: value }));
  };

  // Mirrors the qr/akreditacija rule enforced server-side in
  // gs.rest/routes/validation.js (validateEventGroupOptions): the printed
  // card needs at least one identifying mark. Absence of a key means "on"
  // (see defaultOptions in gs.akr/helpers/queue.js), so this only trips when
  // both are explicitly turned off.
  const hasIdentificationMethod = () =>
    group.dizajnOpcije?.qr !== false || group.dizajnOpcije?.akreditacija !== false;

  // emailTemplateId is optional — a group is usable without a badge template
  // assigned yet (one can be attached later), so it can't be a hard
  // requirement here or the form becomes unsubmittable whenever no
  // EmailTemplates exist yet in the system.
  const validateAllFields = () => {
    const fieldsToValidate = ["naziv"];
    let hasError = false;
    fieldsToValidate.forEach((field) => {
      const value = group[field];

      if (!value) {
        setErrors((prevErrors) => ({
          ...prevErrors,
          [field]: true,
        }));
        hasError = true;
      }
    });

    return !hasError;
  };

  const handleAddClick = async () => {
    setErrors();
    const isValid = validateAllFields();
    if (!isValid) {
      console.log("Postoje greške u formi.");
      return;
    }

    if (!hasIdentificationMethod()) {
      showAlert(t("ACCREDITATION_ID_METHOD_REQUIRED"), "error");
      return;
    }

    setIsLoading(true);
    const sendData = trimValues(group);

    try {
      const result = await createData(
        apiRoutes.addEventGroup,
        sendData,
        showAlert,
        navigate,
        t,
      );

      if (!result) return;

      handleAddData({ ...sendData, id: result.data?.id });
      showAlert(t("SUCCESSFULLY_SAVED"), "success");
      onCloseClick();
    } catch (error) {
      console.error("API poziv nije uspeo:", error.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCompare = (newObject, oldObject) => {
    if (!oldObject) return;

    const changedFields = Object.keys(newObject).reduce((acc, key) => {
      if (key === "old_group") return acc;

      if (JSON.stringify(newObject[key]) !== JSON.stringify(oldObject[key])) {
        acc[key] = newObject[key];
      }

      return acc;
    }, {});

    return changedFields;
  };

  const handleSaveClick = async () => {
    setErrors();
    const isValid = validateAllFields();
    if (!isValid) {
      console.log("Postoje greške u formi.");
      return;
    }

    if (!hasIdentificationMethod()) {
      showAlert(t("ACCREDITATION_ID_METHOD_REQUIRED"), "error");
      return;
    }

    const id = object.id;
    const route = apiRoutes.updateEventGroup.replace(":groupId", id);

    const objekatPromena = handleCompare(group, group.old_group);
    const sendData = trimValues(objekatPromena);

    setIsLoading(true);

    try {
      const result = await updateData(route, sendData, showAlert, navigate, t);
      if (!result) return;

      handleUpdateData({ ...sendData, id: result.data?.id });
      showAlert(t("SUCCESSFULLY_SAVED"), "success");
      onCloseClick();
    } catch (error) {
      console.error("API poziv nije uspeo:", error.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteClick = async () => {
    const route = apiRoutes.deleteEventGroup.replace(":groupId", object.id);
    setIsLoading(true);
    try {
      const result = await deleteData(route, null, showAlert, navigate, t);
      if (!result) return;

      handleDeleteData?.(result.data?.id || object.id);
      showAlert(t("SUCCESSFULLY_DELETED"), "success");
      onCloseClick();
    } catch (error) {
      console.error("API poziv nije uspeo:", error.message);
    } finally {
      setIsLoading(false);
      setIsConfirmDeleteOpen(false);
    }
  };

  useEnterKeySubmit(editing ? handleSaveClick : handleAddClick);

  if (imageLoading) return <Loader />;

  return (
    <>
    <EditModalShell
      isLoading={isLoading}
      t={t}
      headerInfo={
        <p className="inria-sans-400 font-24 modal-title">
          {editing
            ? t("EDIT_EVENT_GROUP_TITLE")
            : parent
              ? `${t("ADDING_SUBGROUP_TO_A_GROUP")} ${parent.naziv}`
              : t("ADD_EVENT_GROUP_TITLE")}
        </p>
      }
      onSave={editing ? handleSaveClick : handleAddClick}
      onClose={onCloseClick}
      onDelete={editing ? () => setIsConfirmDeleteOpen(true) : undefined}
      className="edit-modal-shell-reverse"
      sidePanel={
        <>
          <div className="flex-direction-column gap-24 width-100 person-photo-container">
            <div className="flex-direction-row gap-18 group-modal-image-selector">
              <AddPicture
                setImageLoading={setImageLoading}
                imagePreview={group.dizajnTemplate}
                setInputValue={(value) => setInputValue("slika", value)}
                pdf
                disabled={group.parentDizajn}
              />
              <AccreditationSelector
                options={group?.dizajnOpcije || {}}
                disabled={group.parentDizajn}
                onUpdateOptions={(updatedOptions) => {
                  setGroup((prev) => ({
                    ...prev,
                    dizajnOpcije: updatedOptions,
                  }));
                }}
              />
            </div>
            {parent && (
              <div className="flex-direction-row gap-24 width-100">
                <label className="flex-direction-row gap-14 inria-sans-700 font-12">
                  <input
                    type="checkbox"
                    checked={group.parentDizajn}
                    onChange={(e) =>
                      setInputValue("parentDizajn", e.target.checked)
                    }
                  />
                  {t("USE_DESIGN_FROM_PARENT_GROUP")}
                </label>
              </div>
            )}
          </div>
          {editing && TIER === "third" && (
            <HistoryPanel tabela="dogadjajGrupa" redId={object?.id} />
          )}
        </>
      }
    >
      <div className="flex-direction-column gap-24 width-100 modal-content group-modal">
        <div className="flex-direction-row gap-48 width-100 modal-two-inputs-inline">
          <AccreditationInput
            className="modal-input"
            label={t("NAME_1")}
            setInputValue={(value) => setInputValue("naziv", value)}
            value={group.naziv}
            error={errors?.naziv}
            required={true}
          />
          <AccreditationInput
            className="modal-input"
            label={t("IDENTIFIER")}
            setInputValue={(value) => setInputValue("skracenica", value)}
            value={group.skracenica}
          />
        </div>
        <div className="width-100">
          <AccreditationInput
            className="modal-input"
            label={t("DESCRIPTION")}
            type="text-area"
            setInputValue={(value) => setInputValue("opis", value)}
            value={group.opis}
          />
        </div>
        {templates && templates.length > 0 && (
          <AccreditationInput
            type="dropdown"
            label={t("TEMPLATE")}
            className="modal-input"
            searchable={true}
            selectedOption={
              templates.find((c) => c.id === group?.emailTemplateId)?.naziv ||
              ""
            }
            setSelectedOption={(value) => {
              setInputValue("emailTemplateId", value);
            }}
            options={templates?.map((option) => ({
              key: option.id,
              value: option.naziv,
            }))}
          />
        )}
      </div>
    </EditModalShell>
    {isConfirmDeleteOpen && (
      <ConfirmationModal
        handleDeclineClick={() => setIsConfirmDeleteOpen(false)}
        handleConfirmClick={() => handleDeleteClick()}
        term={group.naziv}
      />
    )}
    </>
  );
};
export default EventGroup;
